import React, { useEffect } from 'react';

import { mobileMessages } from '../../../../translates/mobileMessages';
import { dismissIntroduction } from '../../../actions/baseActions';
import { hotspotCounterTriggered } from '../../../actions/contentItemActions';
import { IntroductionModal } from '../../../components/introduction/IntroductionModal';
import { getMainProjectPanorama } from '../../../globalPanoramaAccessor';
import { useIntlReexport } from '../../../hooks/useIntlReexport';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { getItemByIdTypedSelector } from '../../../store/reducers/items/itemsSelectors';
import { useStudentSelector } from '../../../store/selectors/useStudentSelector';
import { isItemCompletedSelector } from '../../tracking/services/CompletionService';
import { userProgressServiceSelector } from '../../tracking/services/UserProgressService';
import { NoHotspotNotification } from '../containers/NoHotspotNotification';
import { HotspotMapCounter } from './HotspotMapCounter/HotspotMapCounter';
import { HotspotMapCounterButton } from './HotspotMapCoutnerButton/HotspotMapCounterButton';
import { HotspotModal } from './HotspotModal';
import graphic from './non-seq-instruction.svg';
import { useHotspotMapFactory } from './useHotspotMapFactory';
import { isHotspot, NonSequencedFlowItem } from '@ael/core-api';
import { FriendlyNameService, IPanorama } from '@ael/core-api';
import { ZIndex } from '@ael/shared-components-library';
import { useDispatch } from 'react-redux';
import { FroalaEditorsSettings } from 'src/config/configs';

export interface NonSequencedFlowProps {
  nonSequencedFlowItem: NonSequencedFlowItem;
  froalaEditorsSettings: FroalaEditorsSettings;
}

export const NonSequencedFlow: React.FC<NonSequencedFlowProps> = ({ nonSequencedFlowItem, froalaEditorsSettings }) => {
  const { formatMessage } = useIntlReexport();
  const dispatch = useDispatch();
  const getItemByIdTyped = useStudentSelector(getItemByIdTypedSelector);

  const globalPanorama: IPanorama = getMainProjectPanorama();
  const renderHotspotToPanorama = useHotspotMapFactory(globalPanorama);
  const isItemCompleted = useStudentSelector(isItemCompletedSelector)(nonSequencedFlowItem);

  const progressResult = useStudentSelector(userProgressServiceSelector).evaluate(nonSequencedFlowItem);

  const isMobile = useIsMobile();

  useEffect(() => {
    if (nonSequencedFlowItem.startingPosition) {
      globalPanorama.lookToCameraPosition(nonSequencedFlowItem.startingPosition);
    }

    nonSequencedFlowItem.hotspots.forEach((hotspotId: string) => {
      const hotspot = getItemByIdTyped(hotspotId, isHotspot);
      renderHotspotToPanorama(hotspot);
    });

    return () => {
      nonSequencedFlowItem.hotspots.forEach((hotspotId: string) => {
        const friendlyName = FriendlyNameService.getFriendlyName(hotspotId);
        globalPanorama.removeArrow(friendlyName);
        const titleArrowId = FriendlyNameService.getFriendlyName(friendlyName + 'title');
        globalPanorama.removeArrow(titleArrowId);
      });
    };
    // HotspotFactory is disabled here as it depends on isHotspotCompletedSelector thus every hotspot that is finished
    // will result in rerendering this component and the whole re-rendering of all hotspots in panorama just because
    // one hotspot has changed its color. Hotspots are changing their colors themselves in the middleware
    // We want this hook to run only when NonSequencedFlow got changed
    // Because truth to be told 'getItemByIdTyped' never changes as well as 'globalPanorama'
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getItemByIdTyped, globalPanorama, /*hotspotFactory,*/ nonSequencedFlowItem]);

  return (
    <>
      {isMobile && !true ? (
        <IntroductionModal
          onClick={() => dispatch(dismissIntroduction())}
          graphic={<img src={graphic} alt={formatMessage(mobileMessages.tapToOpen)} />}
          header={formatMessage(mobileMessages.tapToExpand)}
          displayDontShowAgain={false}
          buttonLabel={formatMessage(mobileMessages.gotIt)}
        />
      ) : (
        <>
          <HotspotModal onDismiss={() => dispatch(hotspotCounterTriggered())} froalaEditorsSettings={froalaEditorsSettings} />
          <NoHotspotNotification />
          {nonSequencedFlowItem.hotspots.size !== 0 && (
            <HotspotMapCounter
              key={nonSequencedFlowItem.id}
              panorama={globalPanorama}
              hotspots={nonSequencedFlowItem.hotspots}
              isCompleted={isItemCompleted}
            />
          )}
          {!isItemCompleted && (
            <ZIndex index={-1}>
              <HotspotMapCounterButton progress={progressResult} />{' '}
            </ZIndex>
          )}
        </>
      )}
    </>
  );
};
